import React, { useState, useRef, useEffect } from 'react';

import { useAPI } from '../hooks/useAPI';
import useCanvas from '../hooks/useCanvas';

import { AddTextSelector } from './AddTextSelector';
import CanvasArea from './CanvasArea';

const AssetsSelector = ({ assets, selectedCategory, addAssetLayer }) => {
  return (
    <div className="grid grid-cols-4 md:grid-cols-3 items-start justify-start content-start p-2 gap-2 w-full font-bold font-sans overflow-y-auto flex-1 rounded-md">
      {assets.sort((a, b) => a.name.localeCompare(b.name)).map((asset) => (
        asset.category === selectedCategory && (
          <div
          key={asset.id}
          className={`
            cursor-pointer
            aspect-square p-1 rounded-md
            capitalize
            flex
            border border-slate-400/20
            bg-[#eaebed] active:brightness-[103%]
            transition-all duration-300 ease-out
            text-gray-900 dark:text-gray-100
            hover:scale-150 hover:z-50 relative
            `}
            onClick={() => addAssetLayer(asset)}
          >
            <img draggable={false} src={`./assets/${asset.path}`} alt={asset.name} className="m-auto h-full rounded-md" />
          </div>
        )
      ))}
    </div>
  );
};

const MemeGenerator = (props) => {
  const { assets } = useAPI();
  const { clearCanvas, addTextLayer, addAssetLayer, downloadCanvas } = useCanvas();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageUpload = (e) => {
    e.preventDefault();
    const file = e.target.files[0];
    console.log(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        addAssetLayer({
          id: Date.now(),
          type: 'asset',
          path: URL.createObjectURL(file),
          name: file.name,
          category: 'custom',
        });
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const CATEGORIES = assets
      .map((asset) => asset.category)
      .filter((category, index, self) => self.indexOf(category) === index)
      .sort((a, b) => a.localeCompare(b));
    CATEGORIES.push('Text');
    setCategories(CATEGORIES);
    setSelectedCategory(CATEGORIES[0]);
  }, [assets]);

  return (
    <div 
      className={`w-screen h-screen flex flex-col md:flex-row text-gray-900 dark:text-gray-100 ${props.theme === 'dark' ? 'dark' : ''}`}
    >
      {/* Sidebar Area */}
      <aside 
        className="w-full md:w-[300px] md:h-screen flex-1 md:flex-none flex flex-col items-start p-4 gap-2 md:gap-4 z-10 overflow-hidden bg-white dark:bg-gray-900 shadow-lg dark:shadow-none"
      >
        <div className="text-6xl mx-auto dark:text-white">meme generator</div>

        {/* Global controls */}
        <div className="flex flex-row md:flex-col justify-between items-center md:items-start gap-2 w-full font-bold font-sans text-xs md:text-base">
          <button 
            className="w-full bg-[#f2a902] text-white py-2 md:py-1 rounded-md hover:brightness-110 active:brightness-150 transition-all duration-300 cursor-pointer"
            onClick={() => fileInputRef.current.click()}
          >
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageUpload} 
              className="file-input"
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            Upload Image
          </button>
          <button 
            className="w-full py-2 md:py-1 rounded-md transition-all duration-300 cursor-pointer text-slate-700 bg-[#eaebed] hover:brightness-90 active:brightness-110"
            onClick={clearCanvas}
>
            Clear
          </button>
          <button 
            className="w-full md:mt-4 py-2 md:py-1 rounded-md transition-all duration-300 cursor-pointer text-slate-700 bg-[#eaebed] hover:bg-[#f2a902] hover:text-white active:brightness-110"
            onClick={downloadCanvas}
          >
            Download
          </button>
        </div>

        {/* Category selectors */}
        <div className="grid grid-cols-3 md:grid-cols-2 items-center p-2 gap-2 w-full font-bold font-sans">
          {categories.map((category) => (
            <button 
              key={category} 
              className={[
                "w-full py-2 md:px-4 rounded-md transition-all ease-out duration-300 cursor-pointer hover:brightness-90 active:brightness-110 capitalize text-xs", 
                category?.toLowerCase() === selectedCategory?.toLowerCase() ? "bg-[#f2a902] text-white" : "bg-[#eaebed] text-slate-700"
              ].join(" ")}
              onClick={() => setSelectedCategory(category)}
            >
              <span className="text-center">{category}</span>
            </button>
          ))}
        </div>

        {/* Asset selectors */}
        {
          selectedCategory?.toLowerCase() === 'text'
          ? <AddTextSelector addTextLayer={addTextLayer} />
          : <AssetsSelector assets={assets} selectedCategory={selectedCategory} addAssetLayer={addAssetLayer} />
        }
      </aside>
      
      <div 
        className="lg:flex-1 flex items-center justify-center bg-gray-100 dark:bg-gray-800"
      >
        <CanvasArea />
      </div>
    </div>
  );
};

export default MemeGenerator; 